import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Mail, Phone, MessageSquare, RefreshCw, Inbox, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ecn_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-xl border border-[#2E4390] bg-[#0D1B5E] px-4 py-3 text-sm text-[#F8FAFC] placeholder-[#8B9AC4] outline-none transition-colors duration-300 focus:border-[#1E7FFF]";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
        setLeads(data);
        setError("");
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          setLeads(null);
        } else {
          setError(formatApiErrorDetail(e.response?.data?.detail));
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D1B5E] px-5">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl border border-[#2E4390] bg-[#1A2E78]/80 p-8 shadow-[0_0_50px_rgba(30,127,255,0.15)] backdrop-blur-md"
        >
          <div className="flex flex-col items-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2">
              <img src="/assets/ecn-logo.png" alt="ECN Dijital" className="h-full w-full object-contain" />
            </span>
            <h1 className="mt-5 font-syne text-2xl font-bold text-[#F8FAFC]">Admin Girişi</h1>
            <p className="font-code mt-2 text-[10px] tracking-[0.25em] uppercase text-[#8B9AC4]">ECN Dijital Mesaj Paneli</p>
          </div>
          <div className="mt-7 space-y-4">
            <input
              data-testid="admin-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-posta"
              className={inputCls}
            />
            <input
              data-testid="admin-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Şifre"
              className={inputCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-xs text-red-300">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit-btn"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1E7FFF] px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#5CA3FF] disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
              {loading ? "Giriş Yapılıyor..." : "Giriş Yap"}
            </button>
            <a href="/" data-testid="admin-back-to-site" className="block text-center text-xs text-[#8B9AC4] transition-colors hover:text-[#5CA3FF]">
              ← Siteye Dön
            </a>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0D1B5E] text-[#F8FAFC]">
      <header className="sticky top-0 z-40 border-b border-[#2E4390]/60 bg-[#0D1B5E]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white p-1">
              <img src="/assets/ecn-logo.png" alt="ECN Dijital" className="h-full w-full object-contain" />
            </span>
            <div>
              <p className="font-syne text-sm font-bold tracking-[0.15em]">MESAJ PANELİ</p>
              <p className="font-code text-[9px] tracking-[0.2em] uppercase text-[#8B9AC4]">ECN Dijital Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => fetchLeads(token)}
              className="flex items-center gap-2 rounded-full border border-[#2E4390] px-4 py-2 text-xs font-semibold text-[#B9C4E8] transition-colors hover:border-[#1E7FFF] hover:text-[#5CA3FF]"
            >
              <RefreshCw size={13} /> Yenile
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#2E4390] px-4 py-2 text-xs font-semibold text-[#B9C4E8] transition-colors hover:border-red-400 hover:text-red-300"
            >
              <LogOut size={13} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-syne text-2xl font-bold md:text-3xl">İletişim Formu Mesajları</h2>
            <p className="mt-1 text-sm text-[#8B9AC4]">
              Toplam <span data-testid="leads-total-count" className="font-semibold text-[#5CA3FF]">{leads ? leads.length : "…"}</span> mesaj
            </p>
          </div>
        </div>

        {error && (
          <p data-testid="admin-leads-error" className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
        )}

        {loading && !leads && (
          <div className="mt-16 flex items-center justify-center gap-2 text-[#8B9AC4]">
            <Loader2 size={18} className="animate-spin" /> Mesajlar yükleniyor...
          </div>
        )}

        {leads && leads.length === 0 && (
          <div data-testid="leads-empty-state" className="mt-16 flex flex-col items-center gap-3 text-[#8B9AC4]">
            <Inbox size={36} />
            <p className="text-sm">Henüz mesaj yok. İlk mesaj geldiğinde burada listelenecek.</p>
          </div>
        )}

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {leads &&
            leads.map((lead) => (
              <article
                key={lead.id}
                data-testid={`lead-card-${lead.id}`}
                className="rounded-2xl border border-[#2E4390] bg-[#1A2E78]/70 p-6 backdrop-blur-md transition-colors duration-300 hover:border-[#1E7FFF]"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-syne text-base font-bold text-[#F8FAFC]">{lead.full_name}</h3>
                  <span className="font-code shrink-0 text-[10px] tracking-wide text-[#8B9AC4]">
                    {new Date(lead.created_at).toLocaleString("tr-TR")}
                  </span>
                </div>
                <div className="mt-3 space-y-1.5 text-sm text-[#B9C4E8]">
                  <p className="flex items-center gap-2">
                    <Mail size={13} className="shrink-0 text-[#5CA3FF]" />
                    <a href={`mailto:${lead.email}`} className="hover:text-[#5CA3FF]">{lead.email}</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone size={13} className="shrink-0 text-[#5CA3FF]" />
                    <a href={`tel:${lead.phone}`} className="hover:text-[#5CA3FF]">{lead.phone}</a>
                  </p>
                </div>
                <p className="mt-4 flex items-start gap-2 rounded-xl bg-[#0D1B5E]/70 p-4 text-sm leading-relaxed text-[#B9C4E8]">
                  <MessageSquare size={14} className="mt-0.5 shrink-0 text-[#5CA3FF]" />
                  {lead.message}
                </p>
              </article>
            ))}
        </div>
      </main>
    </div>
  );
}
